import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "admin_hide_test_data";

/**
 * Hook for the global admin "Hide test data" toggle.
 *
 * - Default: ON for superadmins, OFF for everyone else.
 * - Persisted in localStorage per browser.
 * - Returns { hideTestData, setHideTestData, excludeEmParam } where
 *   excludeEmParam is the string "1" when on, empty otherwise — ready
 *   to append to API calls.
 */
export function useHideTestData(userRole) {
  const isSuperAdmin = userRole === "superadmin";
  const [hideTestData, setHideTestDataState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw !== null) return raw === "1";
    } catch { /* noop */ }
    return isSuperAdmin; // default ON for superadmin
  });

  // Keep default correct when role loads later
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw === null) setHideTestDataState(isSuperAdmin);
    } catch { /* noop */ }
  }, [isSuperAdmin]);

  const setHideTestData = useCallback((next) => {
    const v = typeof next === "function" ? next(hideTestData) : !!next;
    setHideTestDataState(v);
    try { localStorage.setItem(STORAGE_KEY, v ? "1" : "0"); } catch { /* noop */ }
  }, [hideTestData]);

  return {
    hideTestData,
    setHideTestData,
    excludeEmParam: hideTestData ? "1" : "",
  };
}

/** Returns "?exclude_em=1" (prefixed) when enabled, "" otherwise. */
export function emQueryPrefix(hideTestData) {
  return hideTestData ? "?exclude_em=1" : "";
}

/** Append exclude_em=1 to an existing query string ("?x=1&exclude_em=1"). */
export function appendExcludeEm(queryString, hideTestData) {
  if (!hideTestData) return queryString || "";
  if (!queryString) return "?exclude_em=1";
  return queryString.includes("?") ? `${queryString}&exclude_em=1` : `?${queryString.replace(/^&/, "")}&exclude_em=1`;
}
